import type {
  ContractSignatureArtifactSummary,
  ContractSignatureRequestSummary,
  ContractSignerEventSummary,
} from "@closer/shared";
import {
  CONTRACT_SIGNATURE_REQUEST_STATUS_NAMES,
  CONTRACT_SIGNER_STATUS_NAMES,
  SIGNATURE_ELIGIBILITY_POLICY_NAMES,
  SIGNING_MODE_NAMES,
} from "@closer/shared";
import { Badge, Button, Card, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { TaskReassignDialog } from "../../components/TaskReassignDialog";
import { WorkflowCancelDialog } from "../../components/WorkflowCancelDialog";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";
import { ContractManualSignatureDialog } from "./ContractManualSignatureDialog";
import { ContractSignatureRequestDialog } from "./ContractSignatureRequestDialog";
import { ContractSignerPlanDialog } from "./ContractSignerPlanDialog";

/**
 * İMZA SÜRECİ (CLOS-164 `[157]` · CLOS-165 `[158]` · CLOS-166 `[159]` · CLOS-167 `[160]`).
 *
 * ### ⚠ Ekran KANONİK DURUMU yansıtıyor, kendi durumunu üretmiyor
 *
 * Hangi düğmenin görüneceğine sunucudan gelen `status` karar veriyor: plan yalnız `open`
 * talepte düzenlenebiliyor, imza komutları yalnız `sent` talepte ve yalnız kişinin KENDİ
 * satırında beliriyor. Sunucunun kesin reddedeceği eylemi teklif etmemek bu ürünün kuralı
 * (`ConsentsPage`'in dersi): teklif edilen her düğme bir vaattir.
 *
 * ### ⚠ Karar burada verilmiyor, KAYDEDİLİYOR
 *
 * İmza komutu metnin okunduğu yerde veriliyor: satırlar değişmez versiyona bağlı ve
 * "Metni aç" bağlantısı hep görünür. Ekran imzayı kolaylaştırıyor, imzalanan şeyi
 * gizlemiyor.
 *
 * ### ⚠ Manuel imza AYRI bir eylem
 *
 * Sağlayıcı sonucunu taklit etmiyor (ADR-0035): ayrı düğme, ayrı pencere, yöntem ve kanıt
 * zorunlu. "İmzala" ile aynı düğmeye koysaydık, ıslak imzayı sisteme geçiren kişi ile
 * imzayı atan kişi arasındaki fark ekranda kaybolurdu.
 */
export function ContractSignatureSection({
  contractId,
  arsivde = false,
}: {
  contractId: string;
  /**
   * ⚠ Arşivdeki sözleşmede AKIŞ BAŞLATILMIYOR (kusur avı, 26 Ağustos). Sunucu dördünü de
   * reddediyor ("Arşivdeki sözleşme ... "); düğmeyi çizmek, kullanıcıyı formu doldurup
   * 409 yemeye göndermekti. Yürüyen akışın komutları KALIYOR: arşivleme onları bitirmiyor.
   */
  arsivde?: boolean;
}) {
  const { activeTenantId, can, user } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const [talepAcik, setTalepAcik] = useState(false);
  const [planAcik, setPlanAcik] = useState(false);
  const [manuelHedef, setManuelHedef] = useState<{ requestId: string; signerId: string } | null>(
    null,
  );
  const [gecmisAcik, setGecmisAcik] = useState<string | null>(null);

  const anahtar = tenantKey(tenantId, "contract", contractId, "imza");

  const talepler = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (
        await api<ContractSignatureRequestSummary[]>(
          `/api/contracts/${contractId}/signature-requests`,
          { tenantId },
        )
      ).data,
  });

  /**
   * ⚠ BEKLEYEN İŞLER de tazeleniyor (kusur avı, 26 Ağustos). Bu bölümdeki her karar
   * sunucuda bekleyen görev satırını kapatıyor ya da açıyor; yalnız kendi anahtarını
   * tazeleseydik üst bardaki rozet ve Ana Sayfa bölümü eski sayıda kalır, kullanıcı biraz
   * önce bitirdiği işi hâlâ "bekliyor" diye görürdü. Üç tüketici de burada: panel rozeti
   * (CLOS-156 `[149]`), Ana Sayfa bölümü ve tam liste sayfası.
   */
  const yenile = async () => {
    await queryClient.invalidateQueries({ queryKey: anahtar });
    await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "pending-tasks") });
    await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "home") });
    await queryClient.invalidateQueries({ queryKey: ["pending-tasks"] });
  };

  /** Aktif talep: plan düzenlenebilir (`open`) ya da imza toplanıyor (`sent`). */
  const aktif = (talepler.data ?? []).find((t) => t.status === "open" || t.status === "sent");
  const gecmisTalepler = (talepler.data ?? []).filter((t) => t.id !== aktif?.id);

  const gonder = useMutation({
    mutationFn: async (requestId: string) =>
      api(`/api/contracts/${contractId}/signature-requests/${requestId}/send`, {
        method: "POST",
        tenantId,
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("İmza talebi gönderildi");
    },
    onError: (err: unknown) => toast.error(hataMetni(err, "İmza talebi gönderilemedi")),
  });

  /** Onay bekleyen iptal hedefi; `null` iken diyalog kapalı (CLOS-968). */
  const [iptalHedefi, setIptalHedefi] = useState<string | null>(null);

  /** Devredilecek imzacı satırı; `null` iken yeniden atama diyaloğu kapalı (CLOS-968). */
  const [devirHedef, setDevirHedef] = useState<{
    requestId: string;
    signerId: string;
    signerUserId: string | null;
  } | null>(null);

  /**
   * ⚠ DEVİR KENDİ ANAHTARINI TAŞIYOR (BUG-004). Anahtar niyete bağlı: çift tıklama aynı
   * anahtarı gönderir, sunucu ikincisini replay sayıp ilk yanıtı verir. Devir ile bu
   * bölümdeki diğer komutlar AYRI niyetler; anahtarı paylaşsalardı biri diğerinin yanıtına
   * düşerdi.
   */
  const idempotencyDevir = useIdempotencyKey();

  const iptal = useMutation({
    mutationFn: async (requestId: string) =>
      api(`/api/contracts/${contractId}/signature-requests/${requestId}/cancel`, {
        method: "POST",
        tenantId,
      }),
    onSuccess: async () => {
      setIptalHedefi(null);
      await yenile();
      toast.success("İmza talebi iptal edildi");
    },
    onError: (err: unknown) => toast.error(hataMetni(err, "İmza talebi iptal edilemedi")),
  });

  const komut = useMutation({
    mutationFn: async (girdi: {
      requestId: string;
      signerId: string;
      yol: "view" | "sign" | "decline";
      note?: string;
    }) =>
      api(
        `/api/contracts/${contractId}/signature-requests/${girdi.requestId}/signers/${girdi.signerId}/${girdi.yol}`,
        {
          method: "POST",
          tenantId,
          body: JSON.stringify(girdi.note ? { note: girdi.note } : {}),
        },
      ),
    onSuccess: async () => {
      await yenile();
      toast.success("İmza durumu güncellendi");
    },
    onError: (err: unknown) => toast.error(hataMetni(err, "İmza durumu güncellenemedi")),
  });

  if (!can("contract.sign")) return null;

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">İmza süreci</h2>
        {!aktif && !arsivde ? (
          <Button type="button" size="sm" variant="secondary" onClick={() => setTalepAcik(true)}>
            İmza süreci başlat
          </Button>
        ) : null}
      </div>

      {talepler.isPending ? (
        <Skeleton lines={3} />
      ) : talepler.error ? (
        <StateView
          state={resolveErrorState(talepler.error, {
            idempotent: true,
            overrides: {
              error: {
                title: "İmza süreci yüklenemedi",
                description: "Bölüm şu an getirilemedi.",
              },
            },
          })}
          onRetry={() => void talepler.refetch()}
        />
      ) : (talepler.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">Bu sözleşme için henüz imza süreci başlatılmadı.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {aktif ? (
            <div className="flex flex-col gap-3 rounded-md border border-border-default p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={aktif.status === "sent" ? "warning" : "neutral"}>
                  {CONTRACT_SIGNATURE_REQUEST_STATUS_NAMES[aktif.status]}
                </Badge>
                <span className="text-xs text-fg-soft">
                  Sürüm {aktif.versionNumber} · {SIGNING_MODE_NAMES[aktif.signingMode]} ·{" "}
                  {SIGNATURE_ELIGIBILITY_POLICY_NAMES[aktif.eligibilityPolicy]}
                </span>
                {aktif.expiresAt ? (
                  <span className="text-xs text-fg-soft">
                    Süre: {new Date(aktif.expiresAt).toLocaleDateString("tr-TR")}
                  </span>
                ) : null}
              </div>

              <ImzaciListesi
                talep={aktif}
                kullaniciId={user?.id ?? null}
                onKomut={(signerId, yol) => komut.mutate({ requestId: aktif.id, signerId, yol })}
                onManuel={(signerId) => setManuelHedef({ requestId: aktif.id, signerId })}
                onDevret={(signerId, signerUserId) =>
                  setDevirHedef({ requestId: aktif.id, signerId, signerUserId })
                }
                bekliyor={komut.isPending}
              />

              <div className="flex flex-wrap gap-2">
                {/* ⚠ Plan YALNIZ `open` talepte düzenlenebiliyor (kartın kilit kuralı). */}
                {aktif.status === "open" ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => setPlanAcik(true)}
                    >
                      İmzacıları belirle
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => gonder.mutate(aktif.id)}
                      loading={gonder.isPending}
                      disabled={aktif.signers.length === 0}
                    >
                      Gönder
                    </Button>
                  </>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setIptalHedefi(aktif.id)}
                >
                  Süreci iptal et
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setGecmisAcik(gecmisAcik === aktif.id ? null : aktif.id)}
                >
                  {gecmisAcik === aktif.id ? "Durum geçmişini gizle" : "Durum geçmişi"}
                </Button>
              </div>

              {gecmisAcik === aktif.id ? (
                <DurumGecmisi contractId={contractId} requestId={aktif.id} tenantId={tenantId} />
              ) : null}
            </div>
          ) : null}

          {gecmisTalepler.map((t) => (
            <div key={t.id} className="flex flex-col gap-2 rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={t.status === "completed" ? "success" : "neutral"}>
                  {CONTRACT_SIGNATURE_REQUEST_STATUS_NAMES[t.status]}
                </Badge>
                <span className="text-xs text-fg-soft">
                  Sürüm {t.versionNumber} · {new Date(t.createdAt).toLocaleDateString("tr-TR")}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setGecmisAcik(gecmisAcik === t.id ? null : t.id)}
                >
                  {gecmisAcik === t.id ? "Gizle" : "Durum geçmişi"}
                </Button>
              </div>
              <ImzaciListesi talep={t} kullaniciId={user?.id ?? null} salt />
              {gecmisAcik === t.id ? (
                <DurumGecmisi contractId={contractId} requestId={t.id} tenantId={tenantId} />
              ) : null}
              {t.status === "completed" ? (
                <ArsivKaydi contractId={contractId} requestId={t.id} tenantId={tenantId} />
              ) : null}
            </div>
          ))}
        </div>
      )}

      <ContractSignatureRequestDialog
        open={talepAcik}
        contractId={contractId}
        onClose={() => setTalepAcik(false)}
        onDone={async () => {
          setTalepAcik(false);
          await yenile();
        }}
      />
      {aktif ? (
        <ContractSignerPlanDialog
          open={planAcik}
          contractId={contractId}
          requestId={aktif.id}
          mevcut={aktif.signers}
          onClose={() => setPlanAcik(false)}
          onDone={async () => {
            setPlanAcik(false);
            await yenile();
          }}
        />
      ) : null}
      <ContractManualSignatureDialog
        open={manuelHedef !== null}
        contractId={contractId}
        hedef={manuelHedef}
        onClose={() => setManuelHedef(null)}
        onDone={async () => {
          setManuelHedef(null);
          await yenile();
        }}
      />

      <WorkflowCancelDialog
        open={iptalHedefi !== null}
        onClose={() => setIptalHedefi(null)}
        title="İmza talebini iptal et"
        description="İmza talebi kapanır ve imzacılara gönderilen bağlantılar geçersizleşir. İmza için yeni bir talep oluşturmanız gerekir."
        pending={iptal.isPending}
        onConfirm={() => iptalHedefi && iptal.mutate(iptalHedefi)}
      />

      {/*
       * ⚠ Gerekçe İMZADA ZORUNLU (`config/workflowCancelReassign.json`: signature →
       * `requireReasonForReassign: true`): imzacı değişikliği sözleşmenin kim tarafından
       * bağlandığını değiştiriyor, denetim izinde sebepsiz kalamaz.
       *
       * ⚠ Aday havuzu BU ÇALIŞMA ALANI: `newSignerTenantId` olarak aktif alan gidiyor.
       * Karşı taraftan imzacı seçmek başka bir alanın kullanıcı listesini okumak demek
       * olurdu; o yetki bu ekranda yok ve varmış gibi davranmazdık.
       */}
      <TaskReassignDialog
        open={devirHedef !== null}
        onClose={() => setDevirHedef(null)}
        tenantId={tenantId}
        contractId={contractId}
        title="İmzacıyı devret"
        assigneeLabel="Yeni imzacı"
        reasonRequired
        currentAssigneeUserId={devirHedef?.signerUserId}
        successMessage="İmzacı devredildi"
        fallbackErrorMessage="İmzacı devredilemedi"
        onDone={async () => {
          /** ⚠ Başarıdan sonra yeni niyet yeni anahtar alıyor (BUG-004). */
          idempotencyDevir.yenile();
          await yenile();
        }}
        reassign={async ({ userId, reason }) =>
          api(
            `/api/contracts/${contractId}/signature-requests/${devirHedef!.requestId}/signers/${devirHedef!.signerId}/reassign`,
            {
              method: "POST",
              tenantId,
              headers: { "idempotency-key": idempotencyDevir.uret() },
              body: JSON.stringify({
                newSignerUserId: userId,
                newSignerTenantId: tenantId,
                ...(reason ? { reason } : {}),
              }),
            },
          )
        }
      />
    </Card>
  );
}

function hataMetni(err: unknown, yedek: string): string {
  return err instanceof Error && err.message ? err.message : yedek;
}

/**
 * İmzacı satırları.
 *
 * ⚠ Komutlar YALNIZ kişinin kendi satırında ve yalnız satır beklerken görünüyor: başkası
 * adına imza atılamıyor ve imzalanmış satır yeniden imzalanamıyor.
 */
function ImzaciListesi({
  talep,
  kullaniciId,
  onKomut,
  onManuel,
  onDevret,
  bekliyor,
  salt,
}: {
  talep: ContractSignatureRequestSummary;
  kullaniciId: string | null;
  onKomut?: (signerId: string, yol: "view" | "sign" | "decline") => void;
  onManuel?: (signerId: string) => void;
  onDevret?: (signerId: string, signerUserId: string | null) => void;
  bekliyor?: boolean;
  salt?: boolean;
}) {
  if (talep.signers.length === 0) {
    return <p className="text-sm text-fg-muted">Henüz imzacı belirlenmedi.</p>;
  }

  return (
    <ul className="flex flex-col gap-2">
      {talep.signers.map((imzaci) => {
        const benim = imzaci.signerUserId === kullaniciId;
        const bekliyorMu = imzaci.status === "pending" || imzaci.status === "viewed";
        return (
          <li
            key={imzaci.id}
            className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-1.5 last:border-0"
          >
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm text-fg">
                {imzaci.position + 1}. {imzaci.signerName}
                {imzaci.required ? "" : " (opsiyonel)"}
              </span>
              <span className="truncate text-xs text-fg-soft">{imzaci.partyName}</span>
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <Badge tone={imzaci.status === "signed" ? "success" : "neutral"}>
                {CONTRACT_SIGNER_STATUS_NAMES[imzaci.status]}
              </Badge>
              {!salt && talep.status === "sent" && bekliyorMu ? (
                <>
                  {benim ? (
                    <>
                      {imzaci.status === "pending" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={bekliyor}
                          onClick={() => onKomut?.(imzaci.id, "view")}
                        >
                          Görüntüledim
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        disabled={bekliyor}
                        onClick={() => onKomut?.(imzaci.id, "sign")}
                      >
                        İmzala
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={bekliyor}
                        onClick={() => onKomut?.(imzaci.id, "decline")}
                      >
                        Reddet
                      </Button>
                    </>
                  ) : null}
                  {/* ⚠ Islak imzayı SİSTEME GEÇİREN kişi imzacı değil; ayrı eylem. */}
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => onManuel?.(imzaci.id)}
                  >
                    Manuel imza kaydet
                  </Button>
                  {/*
                   * ⚠ Devretme İMZACI SATIRINI değiştiriyor, imzayı atmıyor (CLOS-968).
                   * Ayrılan ya da yetkisi değişen bir imzacının yerine başkasının
                   * geçebilmesi için tek yol buydu; uç sunucuda vardı, ekranda yoktu.
                   * İmzalanmış satırda görünmüyor: atılmış bir imzanın sahibi değişemez.
                   */}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => onDevret?.(imzaci.id, imzaci.signerUserId)}
                  >
                    Devret
                  </Button>
                </>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Durum geçmişi — tarihsel durum silinmiyor, okunuyor (CLOS-166 `[159]`). */
function DurumGecmisi({
  contractId,
  requestId,
  tenantId,
}: {
  contractId: string;
  requestId: string;
  tenantId: string;
}) {
  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "imza-gecmis", requestId),
    queryFn: async () =>
      (
        await api<ContractSignerEventSummary[]>(
          `/api/contracts/${contractId}/signature-requests/${requestId}/events`,
          { tenantId },
        )
      ).data,
  });

  if (sorgu.isPending) return <Skeleton lines={2} />;
  if (sorgu.error) return <p className="text-sm text-danger">Durum geçmişi yüklenemedi.</p>;
  if ((sorgu.data ?? []).length === 0) {
    return <p className="text-sm text-fg-muted">Henüz durum değişikliği yok.</p>;
  }

  const kaynakAdi: Record<string, string> = {
    user: "kullanıcı",
    provider: "sağlayıcı",
    system: "sistem",
    manual: "manuel kayıt",
  };

  return (
    <ol className="flex flex-col gap-1">
      {(sorgu.data ?? []).map((olay) => (
        <li key={olay.id} className="text-xs text-fg-soft">
          {new Date(olay.occurredAt).toLocaleString("tr-TR")} ·{" "}
          {olay.signerName ? `${olay.signerName}: ` : "Talep: "}
          {olay.fromStatus} → {olay.toStatus} ({kaynakAdi[olay.source] ?? olay.source})
          {olay.note ? ` · ${olay.note}` : ""}
        </li>
      ))}
    </ol>
  );
}

/**
 * İMZALI BELGE ARŞİVİ (CLOS-167 `[160]`).
 *
 * ⚠ Arşiv kaydı imza tamamlanınca DOĞMUŞ oluyor; ekran yalnız onu gösteriyor. İmzalı
 * belge yoksa yükleme yüzeyi açılıyor — belge sonradan bağlanıyor ve bir daha
 * değiştirilemiyor.
 */
function ArsivKaydi({
  contractId,
  requestId,
  tenantId,
}: {
  contractId: string;
  requestId: string;
  tenantId: string;
}) {
  const toast = useToast();
  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "imza-arsiv", requestId),
    queryFn: async () =>
      (
        await api<ContractSignatureArtifactSummary | null>(
          `/api/contracts/${contractId}/signature-requests/${requestId}/artifact`,
          { tenantId },
        )
      ).data,
  });

  /**
   * ⚠ İndirme adresi TIKLANDIĞI AN isteniyor (`[101]` kuralı, ek indirmesiyle aynı):
   * imzalı adres DOM'da durmuyor ve her indirmede erişim kararı yeniden veriliyor.
   * Adresi listeyle birlikte çekseydik, ekranı açan herkesin elinde süreli de olsa
   * çalışan bir bağlantı kalırdı.
   */
  const indir = useMutation({
    mutationFn: async () =>
      (
        await api<{ downloadUrl: string; fileName: string | null }>(
          `/api/contracts/${contractId}/signature-requests/${requestId}/artifact/download-url`,
          { tenantId },
        )
      ).data,
    onSuccess: (bilet) => {
      const bag = document.createElement("a");
      bag.href = bilet.downloadUrl;
      bag.rel = "noopener noreferrer";
      if (bilet.fileName) bag.download = bilet.fileName;
      document.body.appendChild(bag);
      bag.click();
      bag.remove();
    },
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "İmzalı belge indirilemedi"),
  });

  if (sorgu.isPending) return <Skeleton lines={1} />;
  if (sorgu.error || !sorgu.data) return null;

  const arsiv = sorgu.data;
  return (
    <div className="flex flex-col gap-1 rounded-md bg-muted px-3 py-2">
      <span className="text-xs font-medium text-fg-strong">İmzalı belge arşivi</span>
      <span className="text-xs text-fg-soft">
        İmzalanan metnin bütünlük özeti: {arsiv.originalContentHash}
      </span>
      {arsiv.signedObjectId ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-fg-soft">
            İmzalı belge {new Date(arsiv.signedAt!).toLocaleString("tr-TR")} tarihinde
            {arsiv.attachedByName ? ` ${arsiv.attachedByName} tarafından` : ""} arşive bağlandı.
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={indir.isPending}
            onClick={() => indir.mutate()}
          >
            {indir.isPending ? "Hazırlanıyor…" : "İmzalı belgeyi indir"}
          </Button>
        </div>
      ) : (
        <span className="text-xs text-fg-muted">İmzalı belge henüz arşive bağlanmadı.</span>
      )}
    </div>
  );
}
